import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/cronitor')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Cronitor Alternative — Uptrack Monitoring' },
      { name: 'description', content: "Uptrack vs Cronitor: Cronitor bills $2 per monitor and $5 per user each month. Uptrack includes cron and uptime monitors in flat plans from $3/mo." },
      { property: 'og:title', content: 'Cronitor Alternative — Uptrack' },
      { property: 'og:description', content: "Cronitor bills per monitor and per user. Uptrack bundles heartbeat (cron) and uptime monitoring in flat plans from $3/mo." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-cronitor.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/cronitor' },
    ],
    scripts: comparisonScripts('cronitor', "Cronitor"),
  }),
})

const data: ComparisonData = {
  competitor: "Cronitor",
  tagline: "Cronitor monitors cron jobs, heartbeats, and websites, billed at $2 per monitor and $5 per user each month on its Business plan. Uptrack includes cron, heartbeat, and uptime monitors in flat plans from $3/mo.",
  heroDescription: "These facts come from Cronitor's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Cron/heartbeat monitoring", uptrack: "Included (shared monitor pool)", competitor: "$2/monitor/mo (Business)", uptrackWins: true },
    { feature: "Uptime monitoring", uptrack: "Included (shared monitor pool)", competitor: "$2/monitor/mo (Business)", uptrackWins: true },
    { feature: "Users", uptrack: "Included: 3 on Pro, 5 on Team, 15 on Business", competitor: "$5/user/mo (Business)", uptrackWins: true },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "Basic page on free; $25-50/mo per page on Business", uptrackWins: true },
    { feature: "50 monitors", uptrack: "$3/mo (Pro)", competitor: "$100/mo, plus $5 per user", uptrackWins: true },
    { feature: "200 monitors", uptrack: "$20/mo (Team)", competitor: "$400/mo, plus $5 per user", uptrackWins: true },
    { feature: "Free plan", uptrack: "5 HTTP and keyword monitors at 30 s (no cron)", competitor: "5 monitors at 5 min" },
    { feature: "Fastest check interval", uptrack: "30 sec on every plan", competitor: "30 sec (Business)" },
    { feature: "Check regions", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "Up to 12", uptrackWins: MONITOR_LOCATIONS.compare.wins },
  ],

  painPoints: [
    {
      title: "Per-monitor pricing",
      description: "On Cronitor Business, 25 cron jobs and 25 uptime monitors cost $100/mo, plus $5 per user. Uptrack Team covers 200 monitors of any type, with 5 team members, for $20/mo.",
    },
    {
      title: "Status pages cost extra",
      description: "Cronitor's free plan has a basic status page. On Business, a branded status page costs $25/mo and a private one $50/mo. Uptrack includes 5 status pages on Pro and unlimited on Team.",
    },
    {
      title: "Enterprise pricing",
      description: "Cronitor Enterprise starts at $6,000/yr. Uptrack Business, with SSO, whitelabel, and 1,000 monitors, is $588/yr billed annually.",
    },
  ],

  differentiators: [
    "All-in-one: uptime monitoring, cron/heartbeat monitoring, status pages, and alerting in a single tool.",
    "Flat pricing: Pro $3/mo, Team $20/mo, Business $79/mo. No per-monitor or per-user fees as you add jobs.",
    "Alert confirmation: configurable grace periods for heartbeats, and 2-5 consecutive failed checks for uptime monitors.",
    "Included status pages: every plan includes public status pages with email subscriptions. No add-on fees.",
    "Pro includes 50 monitors at 30-second intervals with Email, Slack, Discord, and Telegram alerts.",
  ],

  notForYou: [
    "If you need Cronitor's Terraform/Pulumi provider for infrastructure-as-code monitoring configuration.",
    "If you're already deeply integrated with Cronitor's CLI tool for auto-discovering cron jobs.",
    "If you only need a few monitors, Cronitor has a free plan with 5 monitors, and its per-monitor pricing can cost less at small scale.",
  ],
}
